import * as React from 'react';
import { Button, Form, Input, InputNumber } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

// Form.List field blocks for the project template's items — the deliverables and
// tasks a project inherits when it is created from the template. Presentational
// only: they read and write the enclosing antd v4 Form at the `name` path they are
// given, so they work both at the top level ('tasks') and nested inside a
// deliverable row ([deliverableName, 'tasks']).
//
// Every list on this form shares one idiom — leading position number, trailing
// remove icon, dashed add button — so deliverables, tasks and the required /
// optional field definitions all read the same way.
//
// Rows are deliberately NOT required-validated: a blank row someone added and left
// alone is discarded on save by the clean* helpers in ProjectTemplateForm (which is
// what the pre-antd-v4 form did), rather than blocking the save until it is removed
// by hand.
//
// A template item carries no due date, assignee or status: a template has no
// calendar of its own, so those are set on the project the template creates.

const { TextArea } = Input;

export const TemplateItemRow = ({ index, onRemove, children }) => (
	<div className="flex items-start gap-4">
		<div className="mt-1 w-4 text-character-secondary">{index + 1}</div>
		<div className="flex-1" style={{ maxWidth: 640 }}>
			{children}
		</div>
		<MinusCircleOutlined
			className="mt-2 cursor-pointer text-character-secondary"
			onClick={onRemove}
		/>
	</div>
);

export const TemplateItemAddButton = ({ label, onClick }) => (
	<Form.Item className="mb-0">
		<Button type="dashed" onClick={onClick}>
			<PlusOutlined /> {label}
		</Button>
	</Form.Item>
);

interface TaskListFieldsProps {
	// Path in the enclosing form: 'tasks' or [deliverableIndex, 'tasks'].
	name: string | (string | number)[];
	addLabel: string;
}

export const TemplateTaskListFields: React.FC<TaskListFieldsProps> = ({ name, addLabel }) => (
	<Form.List name={name}>
		{(fields, { add, remove }) => (
			<>
				{fields.map(({ key, name: taskName, ...restField }, index) => (
					<TemplateItemRow key={key} index={index} onRemove={() => remove(taskName)}>
						<Form.Item {...restField} name={[taskName, 'title']} className="mb-2">
							<Input placeholder="Task title" />
						</Form.Item>
					</TemplateItemRow>
				))}
				<TemplateItemAddButton label={addLabel} onClick={() => add()} />
			</>
		)}
	</Form.List>
);

interface DeliverableListFieldsProps {
	name: string;
	// Currency code shown on the budget input, e.g. 'USD'.
	currencyId: string;
}

export const TemplateDeliverableListFields: React.FC<DeliverableListFieldsProps> = ({
	name,
	currencyId,
}) => (
	<Form.List name={name}>
		{(fields, { add, remove }) => (
			<>
				{fields.map(({ key, name: deliverableName, ...restField }, index) => (
					<TemplateItemRow key={key} index={index} onRemove={() => remove(deliverableName)}>
						{/* Left rule groups the deliverable's own fields and its task list,
						    so it stays readable next to the single-field task rows. */}
						<div className="mb-4 border-0 border-l border-solid border-owgray pl-4">
							<Form.Item {...restField} label="Name" name={[deliverableName, 'name']}>
								<Input />
							</Form.Item>
							<Form.Item {...restField} label="Description" name={[deliverableName, 'description']}>
								<TextArea autoSize={{ minRows: 2 }} />
							</Form.Item>
							<Form.Item {...restField} label="Budget" name={[deliverableName, 'budget']}>
								<InputNumber
									min={0}
									style={{ display: 'flex', width: '100%' }}
									addonAfter={currencyId}
								/>
							</Form.Item>
							<div className="bodymedium mb-2">Tasks</div>
							<TemplateTaskListFields name={[deliverableName, 'tasks']} addLabel="Add task" />
						</div>
					</TemplateItemRow>
				))}
				<TemplateItemAddButton label="Add deliverable" onClick={() => add({ tasks: [] })} />
			</>
		)}
	</Form.List>
);
